import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Bug, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import videoApi from "@/lib/api";
import { sanitizeTextInput, sanitizeTextInputNoTrim } from "@/lib/security";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";

export const IssueReportDialog = ({ open, onOpenChange }) => {
  const { language } = useLanguage();
  const location = useLocation();
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const currentVideoId = useMemo(() => {
    const match = location.pathname.match(/^\/video\/([0-9a-f-]{36})$/i);
    return match?.[1] || null;
  }, [location.pathname]);

  const copy = useMemo(() => language === "es" ? {
    title: "Reportar problema",
    description: "Cuéntanos qué falló y revisaremos el caso desde el inbox admin.",
    messageLabel: "Describe el problema",
    messagePlaceholder: "Ejemplo: el momento clave salta de 00:08 a 1:00:00 y faltan eventos intermedios.",
    pageLabel: "Ruta actual",
    videoLabel: "Video vinculado",
    cancel: "Cancelar",
    submit: "Enviar reporte",
    sending: "Enviando...",
    success: "Reporte enviado correctamente",
    error: "No pudimos enviar tu reporte",
    minMessage: "Describe el problema con un poco más de detalle.",
  } : {
    title: "Report issue",
    description: "Tell us what failed and we’ll review it from the admin inbox.",
    messageLabel: "Describe the issue",
    messagePlaceholder: "Example: key moments jump from 00:08 to 1:00:00 and miss the middle events.",
    pageLabel: "Current route",
    videoLabel: "Linked video",
    cancel: "Cancel",
    submit: "Send report",
    sending: "Sending...",
    success: "Issue report sent successfully",
    error: "We couldn’t send your report",
    minMessage: "Please add a bit more detail about the issue.",
  }, [language]);

  useEffect(() => {
    if (!open) {
      setMessage("");
    }
  }, [open]);

  const handleSubmit = async () => {
    const cleanedMessage = sanitizeTextInput(message, 2000);
    if (cleanedMessage.length < 10) {
      toast.error(copy.minMessage);
      return;
    }

    try {
      setSubmitting(true);
      await videoApi.reportIssue({
        message: cleanedMessage,
        page_path: location.pathname,
        video_id: currentVideoId,
      });
      toast.success(copy.success);
      onOpenChange(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || copy.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="issue-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="h-4 w-4 text-primary" />
            {copy.title}
          </DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-2xl border border-border bg-muted/20 p-4 text-sm" data-testid="issue-report-context-card">
            <div className="flex flex-wrap gap-4 text-muted-foreground">
              <div data-testid="issue-report-page-path"><span className="font-medium text-foreground">{copy.pageLabel}:</span> {location.pathname}</div>
              {currentVideoId && (
                <div data-testid="issue-report-video-id"><span className="font-medium text-foreground">{copy.videoLabel}:</span> {currentVideoId}</div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="issue-report-message">{copy.messageLabel}</label>
            <Textarea
              id="issue-report-message"
              value={message}
              onChange={(event) => setMessage(sanitizeTextInputNoTrim(event.target.value, 2000))}
              placeholder={copy.messagePlaceholder}
              className="min-h-[180px]"
              data-testid="issue-report-message-input"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="issue-report-cancel-button">
            {copy.cancel}
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={submitting} data-testid="issue-report-submit-button">
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {submitting ? copy.sending : copy.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};